import { ReactNode } from 'react';
import { toast, ToastOptions } from 'react-toastify';
import { cn } from '@restworld/utils-common';
import { XMarkIcon, InformationCircleIcon, EyeIcon } from '@heroicons/react/24/solid';

import IconButton from '../icon-button';

export const showToast = (
  message: ReactNode,
  variant: ToastVariantType,
  config?: ToastOptions,
  hideCrossIcon?: boolean,
  hidePrefixIcon?: boolean,
  wrapperStyle?: string
) => {
  let icon: ReactNode;
  switch (variant) {
    case 'reversed':
      icon = <EyeIcon className="h-6 w-6 fill-neutral-10" />;
      break;
    case 'restworld':
      icon = '🎉';
      break;
    default:
      icon = <InformationCircleIcon className="h-6 w-6" />;
  }

  toast(
    ({ closeToast }) => (
      <CustomToast
        message={message}
        variant={variant}
        closeToast={closeToast}
        icon={icon}
        hideCrossIcon={hideCrossIcon}
        hidePrefixIcon={hidePrefixIcon}
        wrapperStyle={wrapperStyle}
      />
    ),
    {
      ...config,
    }
  );
};

export const closeAllToasts = () => toast.dismiss();

const CustomToast = ({
  message,
  closeToast,
  variant,
  icon,
  hideCrossIcon,
  hidePrefixIcon,
  wrapperStyle,
}: CustomToastProps) => (
  <div className="p-4 bg-transparent md:p-0">
    <div
      className={cn(
        'p-4 flex items-center justify-between gap-2 rounded-lg w-auto max-w-[525px] shadow-toast-dark lg:gap-4',
        variant === 'restworld' && 'bg-purple-500 text-neutral-10',
        variant === 'reversed' && 'bg-neutral-700 text-neutral-10',
        variant === 'success' && 'bg-green-50 text-green-700',
        variant === 'error' && 'bg-red-50 text-red-700',
        wrapperStyle
      )}
    >
      <div className="flex items-center gap-2 text-[14px] font-medium lg:text-[16px] lg:gap-4">
        {!hidePrefixIcon && icon && <span>{icon}</span>}
        <span>{message}</span>
      </div>
      {!hideCrossIcon && (
        <div className="min-w-[40px]">
          <IconButton
            ariaLabel="close"
            Icon={XMarkIcon}
            onClick={closeToast}
            variant={variant === 'restworld' ? 'reversed' : variant}
          />
        </div>
      )}
    </div>
  </div>
);

export type ToastVariantType = 'restworld' | 'reversed' | 'success' | 'error';
interface CustomToastProps {
  message: ReactNode;
  variant: ToastVariantType;
  closeToast: () => void;
  icon?: ReactNode;
  hideCrossIcon?: boolean;
  hidePrefixIcon?: boolean;
  wrapperStyle?: string;
}
